import type { TouchMovementInput } from "../player";

export interface TouchControlsCallbacks {
  onMove: (input: TouchMovementInput) => void;
  onLook: (deltaX: number, deltaY: number) => void;
  onInteract: () => void;
  onCrouchChange: (crouching: boolean) => void;
  onPause: () => void;
}

export class TouchControls {
  private readonly root: HTMLElement;
  private readonly callbacks: TouchControlsCallbacks;
  private readonly abortController = new AbortController();
  private readonly joystickZone: HTMLElement;
  private readonly joystickKnob: HTMLElement;
  private readonly lookZone: HTMLElement;
  private readonly interactButton: HTMLButtonElement;
  private readonly crouchButton: HTMLButtonElement;
  private readonly hint: HTMLElement;
  private joystickPointer: number | null = null;
  private lookPointer: number | null = null;
  private lookX = 0;
  private lookY = 0;
  private crouching = false;
  private hintTimeout: number | null = null;

  constructor(container: HTMLElement, callbacks: TouchControlsCallbacks) {
    this.callbacks = callbacks;
    this.root = document.createElement("div");
    this.root.className = "mobile-controls is-hidden";
    this.root.setAttribute("aria-hidden", "true");
    this.root.innerHTML = `
      <div class="touch-look-zone" aria-hidden="true"></div>
      <div class="joystick-zone" aria-label="移动摇杆">
        <div class="joystick-base"><div class="joystick-knob"></div></div>
      </div>
      <div class="touch-actions">
        <button type="button" class="touch-action touch-interact" aria-label="互动">互动</button>
        <button type="button" class="touch-action touch-crouch" aria-label="蹲下" aria-pressed="false">蹲下</button>
      </div>
      <button type="button" class="touch-pause" aria-label="暂停散步"><span aria-hidden="true"></span></button>
      <p class="touch-controls-hint">左侧移动 · 右侧拖动视角</p>`;
    container.append(this.root);

    const joystickZone = this.root.querySelector<HTMLElement>(".joystick-zone");
    const joystickKnob = this.root.querySelector<HTMLElement>(".joystick-knob");
    const lookZone = this.root.querySelector<HTMLElement>(".touch-look-zone");
    const interactButton = this.root.querySelector<HTMLButtonElement>(".touch-interact");
    const crouchButton = this.root.querySelector<HTMLButtonElement>(".touch-crouch");
    const pauseButton = this.root.querySelector<HTMLButtonElement>(".touch-pause");
    const hint = this.root.querySelector<HTMLElement>(".touch-controls-hint");
    if (!joystickZone || !joystickKnob || !lookZone || !interactButton || !crouchButton || !pauseButton || !hint) {
      throw new Error("Touch controls were not created.");
    }
    this.joystickZone = joystickZone;
    this.joystickKnob = joystickKnob;
    this.lookZone = lookZone;
    this.interactButton = interactButton;
    this.crouchButton = crouchButton;
    this.hint = hint;
    this.bind(pauseButton);
  }

  show(): void {
    this.root.classList.remove("is-hidden");
    this.root.setAttribute("aria-hidden", "false");
    this.showHint();
  }

  hide(): void {
    this.root.classList.add("is-hidden");
    this.root.setAttribute("aria-hidden", "true");
    this.resetMovement();
  }

  setInteractionAvailable(available: boolean): void {
    this.interactButton.classList.toggle("is-available", available);
    this.interactButton.setAttribute("aria-disabled", String(!available));
  }

  dispose(): void {
    this.abortController.abort();
    if (this.hintTimeout !== null) window.clearTimeout(this.hintTimeout);
    this.root.remove();
  }

  private bind(pauseButton: HTMLButtonElement): void {
    const signal = this.abortController.signal;
    this.joystickZone.addEventListener("pointerdown", this.handleJoystickStart, { signal });
    this.joystickZone.addEventListener("pointermove", this.handleJoystickMove, { signal });
    this.joystickZone.addEventListener("pointerup", this.handleJoystickEnd, { signal });
    this.joystickZone.addEventListener("pointercancel", this.handleJoystickEnd, { signal });
    this.lookZone.addEventListener("pointerdown", this.handleLookStart, { signal });
    this.lookZone.addEventListener("pointermove", this.handleLookMove, { signal });
    this.lookZone.addEventListener("pointerup", this.handleLookEnd, { signal });
    this.lookZone.addEventListener("pointercancel", this.handleLookEnd, { signal });
    this.interactButton.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      this.interactButton.classList.add("is-pressed");
      this.callbacks.onInteract();
      navigator.vibrate?.(12);
    }, { signal });
    this.interactButton.addEventListener("pointerup", () => this.interactButton.classList.remove("is-pressed"), { signal });
    this.interactButton.addEventListener("pointercancel", () => this.interactButton.classList.remove("is-pressed"), { signal });
    this.crouchButton.addEventListener("click", () => {
      this.crouching = !this.crouching;
      this.crouchButton.setAttribute("aria-pressed", String(this.crouching));
      this.crouchButton.classList.toggle("is-active", this.crouching);
      this.callbacks.onCrouchChange(this.crouching);
      navigator.vibrate?.(10);
    }, { signal });
    pauseButton.addEventListener("click", () => {
      navigator.vibrate?.(8);
      this.callbacks.onPause();
    }, { signal });
  }

  private readonly handleJoystickStart = (event: PointerEvent): void => {
    if (this.joystickPointer !== null) return;
    event.preventDefault();
    this.joystickPointer = event.pointerId;
    try { this.joystickZone.setPointerCapture(event.pointerId); } catch { /* Some WebViews omit pointer capture. */ }
    this.updateJoystick(event);
  };

  private readonly handleJoystickMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.joystickPointer) return;
    event.preventDefault();
    this.updateJoystick(event);
  };

  private readonly handleJoystickEnd = (event: PointerEvent): void => {
    if (event.pointerId !== this.joystickPointer) return;
    this.joystickPointer = null;
    this.resetMovement();
  };

  private updateJoystick(event: PointerEvent): void {
    const rect = this.joystickZone.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const radius = Math.min(rect.width, rect.height) * 0.32;
    const dx = event.clientX - centerX;
    const dy = event.clientY - centerY;
    const distance = Math.hypot(dx, dy);
    const scale = distance > radius ? radius / distance : 1;
    const x = dx * scale;
    const y = dy * scale;
    this.joystickKnob.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    const strength = Math.min(1, distance / radius);
    const deadZone = 0.12;
    const adjusted = strength <= deadZone ? 0 : (strength - deadZone) / (1 - deadZone);
    const directionX = distance > 0 ? dx / distance : 0;
    const directionZ = distance > 0 ? -dy / distance : 0;
    this.callbacks.onMove({
      x: directionX * adjusted,
      z: directionZ * adjusted,
      running: strength > 0.82,
    });
  }

  private resetMovement(): void {
    this.joystickPointer = null;
    this.joystickKnob.style.transform = "translate3d(0, 0, 0)";
    this.callbacks.onMove({ x: 0, z: 0, running: false });
  }

  private readonly handleLookStart = (event: PointerEvent): void => {
    if (this.lookPointer !== null) return;
    event.preventDefault();
    this.lookPointer = event.pointerId;
    this.lookX = event.clientX;
    this.lookY = event.clientY;
    try { this.lookZone.setPointerCapture(event.pointerId); } catch { /* Drag tracking still works without capture. */ }
  };

  private readonly handleLookMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.lookPointer) return;
    event.preventDefault();
    const deltaX = event.clientX - this.lookX;
    const deltaY = event.clientY - this.lookY;
    this.lookX = event.clientX;
    this.lookY = event.clientY;
    this.callbacks.onLook(deltaX, deltaY);
  };

  private readonly handleLookEnd = (event: PointerEvent): void => {
    if (event.pointerId === this.lookPointer) this.lookPointer = null;
  };

  private showHint(): void {
    this.hint.classList.add("is-visible");
    if (this.hintTimeout !== null) window.clearTimeout(this.hintTimeout);
    this.hintTimeout = window.setTimeout(() => this.hint.classList.remove("is-visible"), 4200);
  }
}
